import * as React from 'react';
import classnames from 'classnames';

import {EpiImage} from "features/search/types";
import {IContentBlock} from 'features/blocks/types';
import {ImageSizes} from '@organisms/Shell/image-size-enum';
import {ModalType, useModal} from 'features/modal/hooks/useModal';
import {gtmPageView} from 'features/services/googleTagManager/events';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import { HeadlineLevel, LinkStyle, Margin, MarginVariant, ReadMoreAlign } from 'features/ui/enums';

import 'components/blocks/Link/ImageLinkBlockItem/style.scss';

import {A} from '@atoms/LinksAndButtons/A';
import {P} from '@atoms/Text/P';
import {Icon} from '@atoms/Media/Icon';
import {Button, ButtonColor, ButtonSize, ButtonType} from '@atoms/LinksAndButtons/Button';
import {ReadMore} from '@atoms/LinksAndButtons/ReadMore';
import {AsyncImage} from '@atoms/Media/AsyncImage';
import {InspirationPage} from "@pages/InspirationPage";
import { getPageViewModel } from '@organisms/BuyResidenceGeneral/services/api';
import {Headline} from '@atoms/Text/Headline';
import { useDeviceStore, selectIsMobile, selectIsTablet } from 'features/ui/store/useDeviceStore';

export interface ImageLinkBlockItemType {
    title: string;
    content: string;
    image: EpiImage;
    icon: string;
    linkText: string;
    linkUrl: string;
    modalSubPage: string;
}
interface Props extends IContentBlock<ImageLinkBlockItemType>{
    bigImageSize?: boolean;
    smallSliderCard?: boolean;
    sliderItem?: boolean;
}

export const ImageLinkBlockItem: React.FunctionComponent<Props> = ({
    data, bigImageSize, smallSliderCard, sliderItem, id
}) => {
    const { open } = useModal();
    const isMobile = useDeviceStore(selectIsMobile);
    const isTablet = useDeviceStore(selectIsTablet);

    const onModalSubPageClick = async (e) => {
        e.preventDefault();

        const vm = await getPageViewModel(data.modalSubPage);
        gtmPageView("onModalSubPageClick", document.title, data.modalSubPage, "", "", false)();
        open(
            <InspirationPage viewModel={vm.template.viewModel} />,
            { fullscreen: true, type: ModalType.INSPIRATION, triggerElementId: id, ariaLabel: getFormattedMessage('jm.components.inspirationModal.ariaLabel') }
        );
    };

    const renderContent = () => {
        return (
            <React.Fragment>
                {data.icon && (!data.image || data.image.url.length < 1) && data.icon !== "block-no-icon" &&
                    <div className="image-link-block-item__link">
                        <Icon name={data.icon.toLowerCase()} />
                    </div>
                }
                {data.image && data.image.url.length > 0 &&
                    <div className="image-link-block-item__image">
                        <AsyncImage
                            fullHeight
                            image={data.image}
                            quality={
                                bigImageSize
                                    ? ImageSizes.SIZE_900
                                    : ImageSizes.SIZE_400
                            }
                            alt={data.image.altText}
                        />
                    </div>
                }
                {(data.title || data.content || data.linkUrl || data.modalSubPage) &&
                    <div className="image-link-block-item__text">
                        {data.title &&
                            <>
                                {smallSliderCard
                                    ? <Headline level={HeadlineLevel.H5}>{data.title}</Headline>
                                    : <Headline level={HeadlineLevel.H3} center>{data.title}</Headline>
                                }
                            </>
                        }
                        {data.content &&
                            <P
                                center={!smallSliderCard}
                                margin={(isMobile && !isTablet)
                                    ? Margin.S
                                    : Margin.M
                                }
                                marginVariant={(isMobile && !isTablet)
                                    ? MarginVariant.CENTER
                                    : undefined
                                }
                            >
                                {data.content}
                            </P>
                        }

                        {data.linkUrl &&
                            <ReadMore
                                block
                                align={!smallSliderCard ? ReadMoreAlign.CENTER : ReadMoreAlign.DEFAULT}
                                text={
                                    data.linkText
                                        ? data.linkText
                                        : getFormattedMessage('jm.components.AreaPage.readMore')
                                }
                            />
                        }
                        {data.modalSubPage && !data.linkUrl &&
                            <Button 
                                buttonColor={ButtonColor.DEFAULT} 
                                buttonSize={(!smallSliderCard && bigImageSize) ? ButtonSize.WIDE : ButtonSize.DEFAULT} 
                                buttonType={ButtonType.GHOST}
                            >
                                {data.linkText
                                    ? data.linkText
                                    : getFormattedMessage('jm.components.AreaPage.readMore')
                                } +
                            </Button>
                        }
                    </div>
                }
            </React.Fragment>
        )
    };

    const itemClasses = classnames('image-link-block-item', {
        'image-link-block-item--image': (data.image && data.image.url.length > 0),
        'image-link-block-item--icon': (data.icon && (!data.image || data.image.url.length < 1) && data.icon !== "block-no-icon"),
        'image-link-block-item--slider-item': sliderItem,
        'image-link-block-item--slider-item--wide': (sliderItem && !smallSliderCard),
    });

    if(!data.linkUrl && !data.modalSubPage) {
        return (
            <div className={itemClasses} id={id}>
                {renderContent()}
            </div>
        )
    }

    return (
        <A
            className={itemClasses}
            href={data.linkUrl
                ? data.linkUrl
                : data.modalSubPage
            }
            id={id}
            variant={LinkStyle.UNSTYLED}
            onClick={data.modalSubPage
                ? onModalSubPageClick
                : () => {}
            }
            route={data.linkUrl
                ? data.linkUrl.indexOf('http') === -1 && data.linkUrl.indexOf('mailto:') === -1 && data.linkUrl.indexOf('tel:') === -1
                : true
            }
            title={data.title
                ? data.title
                : getFormattedMessage('jm.components.AreaPage.readMore')} 
        >
            {renderContent()}
        </A>
    )
};

